// components/ErrorReportDialog.js
import React, { useState } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Textarea,
    Select,
    Option,
    Spinner
} from "@material-tailwind/react";
import { AlertTriangle, Send, X } from 'lucide-react';
import { useLogError } from '../services/contexts/logErrorContext';
import { GetCurrentUser } from '../services/firebase/auth';

export const ErrorReportDialog = () => {
    const {
        isReportDialogOpen,
        errorToReport,
        isSubmitting,
        closeReportDialog,
        reportError
    } = useLogError();

    const [additionalInfo, setAdditionalInfo] = useState('');
    const [severity, setSeverity] = useState('medium');

    const handleSubmit = async () => {
        if (!errorToReport) return;

        const errorDetails = {
            ...errorToReport,
            additionalInfo,
            severity,
            userId: getCurrentUserId() // You'll need to implement this function
        };

        await reportError(errorDetails);
        setAdditionalInfo('');
        setSeverity('medium');
    };

    const getCurrentUserId = () => {
        // Implement this based on your auth system
        // For example, if using Firebase Auth:
        // return auth.currentUser?.uid || 'anonymous';
        const user = GetCurrentUser();
        return user?.uid || 'anonymous'; // Default fallback
    };

    if (!isReportDialogOpen || !errorToReport) return null;

    return (
        <Dialog open={isReportDialogOpen} handler={closeReportDialog} size="md" className="p-4 rounded-3xl">
            <DialogHeader className="flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={24} />
                <Typography variant="h5" color="blue-gray">
                    Report Error
                </Typography>
            </DialogHeader>

            <DialogBody className="space-y-4">
                <div>
                    <Typography variant="h6" color="blue-gray" className="mb-2">
                        Error Details
                    </Typography>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <Typography variant="small" color="red" className="font-mono">
                            {errorToReport.message}
                        </Typography>
                    </div>
                </div>

                <div>
                    <Typography variant="h6" color="blue-gray" className="mb-2">
                        Severity Level
                    </Typography>
                    <Select
                        value={severity}
                        onChange={(val) => setSeverity(val)}
                        label="How critical is this error?"
                    >
                        <Option value="low">Low - Minor inconvenience</Option>
                        <Option value="medium">Medium - Noticeable issue</Option>
                        <Option value="high">High - Major functionality broken</Option>
                        <Option value="critical">Critical - System unusable</Option>
                    </Select>
                </div>

                <div>
                    <Typography variant="h6" color="blue-gray" className="">
                        Additional Information
                    </Typography>
                    <p className="text-xs text-gray-500 mb-2">Please describe what you were doing when this error occurred, steps to reproduce, or any other relevant information...</p>
                    <Textarea
                        value={additionalInfo}
                        onChange={(e) => setAdditionalInfo(e.target.value)}
                        rows={4}
                        label="Description (Optional)"
                    />
                    <p className="text-xs text-gray-500">This information will help us understand and fix the issue faster.</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <Typography variant="small" color="blue-gray" className="text-xs">
                        <strong>Technical Information:</strong><br />
                        • Browser: {navigator.userAgent}<br />
                        • URL: {window.location.href}<br />
                        • Timestamp: {new Date(errorToReport.timestamp).toLocaleString()}
                    </Typography>
                </div>
            </DialogBody>

            <DialogFooter className="flex gap-2">
                <Button
                    variant="text"
                    onClick={closeReportDialog}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 rounded-full"
                >
                    <X size={16} />
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 rounded-full"
                    color="blue"
                >
                    {isSubmitting ? (
                        <Spinner size="sm" />
                    ) : (
                        <Send size={16} />
                    )}
                    {isSubmitting ? 'Reporting...' : 'Report Error'}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};